/**
 * MobileStackedShell — Phase 2d (vibe-designer 1.127.0) + Phase 2e (1.128.0).
 *
 * Single-pane stacked layout for touch viewports: 4 panes (Pi Chat, Editor,
 * Preview, Info) keyed by uiStore.activeMobilePanel. Active pane gets
 * `display: contents` (so its children participate in the parent flex
 * flow as if the wrapper didn't exist); inactive panes get
 * `display: none` (removed from layout AND accessibility tree).
 *
 * Phase 2e (1.128.0): Files moved out of the tab bar to a dedicated
 * left-edge FilesDrawer mounted as a sibling at AppShell level. A "Files"
 * button (OUTSIDE role="tablist" per WAI-ARIA §5.3.3) opens the drawer.
 *
 * Mount discipline: all 4 panes stay mounted at all times. Tab switching
 * is purely a display toggle — no React unmount/remount. Mirrors
 * DualShellLayout's `display: contents`/`none` precedent (E.21.24).
 *
 * Files → Editor auto-switch (Phase 2e): when filesDrawerOpen=true AND
 * projectStore.activeFileId changes — whether by FilesPanel row tap or
 * Cmd+P palette — the effect below closes the drawer + flips active to
 * 'editor'. Drawer-still-open while editor shows a different file is
 * confusing UX; auto-close is "by design."
 *
 * Tab bar position: TOP. iOS convention is bottom but bottom collides
 * with the iPhone home indicator on portrait. Phase 2d.1 may revisit.
 *
 * NOTE: only renders inside AppShell when isTouch=true. Real touch
 * hardware sets isTouch once at mount; DevTools "Toggle device toolbar"
 * is the only path that flips it mid-session.
 */
import { useEffect, useRef } from 'react';
import { Code, Eye, MessageSquare, Folder, AlertCircle } from 'lucide-react';
import clsx from 'clsx';
import { useUIStore, type MobilePanel } from '@/stores/uiStore';
import { useProjectStore } from '@/stores/projectStore';

interface MobileStackedShellProps {
  // 1.128.0 Phase 2e: `files` prop dropped — Files moved to dedicated
  // FilesDrawer at AppShell level. MobileStackedShell now hosts 4 panes.
  editor: React.ReactNode;
  preview: React.ReactNode;
  chat: React.ReactNode;
  info: React.ReactNode;
}

const TAB_DEFS: {
  id: MobilePanel;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  { id: 'chat', label: 'Chat', icon: MessageSquare },
  { id: 'editor', label: 'Editor', icon: Code },
  { id: 'preview', label: 'Preview', icon: Eye },
  { id: 'info', label: 'Info', icon: AlertCircle },
];

export function MobileStackedShell({
  editor,
  preview,
  chat,
  info,
}: MobileStackedShellProps) {
  const active = useUIStore((s) => s.activeMobilePanel);
  const setActive = useUIStore((s) => s.setActiveMobilePanel);
  const filesDrawerOpen = useUIStore((s) => s.filesDrawerOpen);
  const setFilesDrawerOpen = useUIStore((s) => s.setFilesDrawerOpen);
  const activeFileId = useProjectStore((s) => s.activeFileId);

  // 1.128.0 Phase 2e: Files-drawer-open + Editor auto-switch.
  // Triggers for ANY activeFileId change while filesDrawerOpen=true —
  // including Cmd+P palette opens. By design: drawer-still-open while
  // editor shows a different file is confusing UX.
  const prevActiveFileIdRef = useRef<string | null>(activeFileId ?? null);
  useEffect(() => {
    const prev = prevActiveFileIdRef.current;
    const next = activeFileId ?? null;
    prevActiveFileIdRef.current = next;
    if (next && next !== prev && filesDrawerOpen) {
      setActive('editor');
      setFilesDrawerOpen(false);
    }
  }, [activeFileId, filesDrawerOpen, setActive, setFilesDrawerOpen]);

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* 1.128.0 Phase 2e: outer flex row — Files button (NOT in tablist
          per WAI-ARIA 1.2 §5.3.3) + tablist as siblings. */}
      <div className="flex items-center border-b border-vibe-border bg-vibe-bg-secondary shrink-0">
        <button
          type="button"
          data-testid="mobile-stacked-files-button"
          aria-label="Open files"
          onClick={() => setFilesDrawerOpen(true)}
          className="flex flex-col items-center justify-center gap-0.5 px-3 py-2 text-[10px] min-h-[44px] border-r border-vibe-border text-vibe-text-secondary hover:text-vibe-text"
        >
          <Folder className="w-4 h-4" />
          Files
        </button>
        <div
          role="tablist"
          aria-label="Mobile panel switcher"
          data-testid="mobile-stacked-tab-bar"
          className="flex flex-1 items-center"
        >
          {TAB_DEFS.map((tab) => {
            const isActive = active === tab.id;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                role="tab"
                type="button"
                aria-selected={isActive}
                data-testid={`mobile-stacked-tab-${tab.id}`}
                onClick={() => setActive(tab.id)}
                className={clsx(
                  'flex-1 flex flex-col items-center justify-center gap-0.5 px-2 py-2 text-[10px] transition-colors min-h-[44px]',
                  isActive
                    ? 'text-vibe-accent border-b-2 border-vibe-accent -mb-px bg-vibe-bg/40'
                    : 'text-vibe-text-secondary hover:text-vibe-text',
                )}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Panel stack — each pane stays mounted; only display flips.
          DualShellLayout E.21.24 precedent. flex-1 + overflow-hidden
          gives each active pane the remaining height after tab bar. */}
      <div className="flex-1 relative overflow-hidden">
        <div
          data-testid="mobile-stacked-pane-chat"
          style={{ display: active === 'chat' ? 'contents' : 'none' }}
        >
          {chat}
        </div>
        <div
          data-testid="mobile-stacked-pane-editor"
          style={{ display: active === 'editor' ? 'contents' : 'none' }}
        >
          {editor}
        </div>
        <div
          data-testid="mobile-stacked-pane-preview"
          style={{ display: active === 'preview' ? 'contents' : 'none' }}
        >
          {preview}
        </div>
        {/* 1.128.0 Phase 2e: files pane removed; FilesPanel now lives
            inside FilesDrawer mounted as sibling at AppShell level. */}
        <div
          data-testid="mobile-stacked-pane-info"
          style={{ display: active === 'info' ? 'contents' : 'none' }}
        >
          {info}
        </div>
      </div>
    </div>
  );
}

export default MobileStackedShell;
